import { useCallback, useEffect, useRef, useState } from 'react'
import { useClient } from '@/hooks/useClient'
import { logError, logWarning, getErrorMessage } from '@/utils/errorUtils'
import { isUnavailableConversationError, type ChatEntry } from '@/pages/Assistant/useAssistant'

/**
 * Lean in-call sibling of the Assistant page's `useAssistant` — that hook is a
 * page monolith (boot, conversation list, debug tools, delete) unusable in the
 * rail. This one drives a single patient-pinned conversation: lazy
 * `assistantStart({ context })` on the first question, send, SSE stream,
 * cancel, and re-attach via `assistantGet` when the rail remounts with an
 * existing thread. The threadPath itself lives in CallSessionProvider so it
 * survives ZoomPanel remounts (rail↔fullscreen and route changes mid-call).
 */
export function useAssistantThread(opts: {
  patientId: string
  threadPath: string[] | null
  onThreadPathChange: (path: string[] | null) => void
}): {
  entries: ChatEntry[]
  streaming: boolean
  error: string | null
  send: (text: string) => Promise<'sent' | 'already_streaming'>
  cancel: () => Promise<void>
} {
  const { patientId, threadPath, onThreadPathChange } = opts
  const client = useClient()
  const [entries, setEntries] = useState<ChatEntry[]>([])
  const [streaming, setStreaming] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Refs mirror state so the stream loop and send (long-lived async closures)
  // read current values without re-subscribing per render.
  const entriesRef = useRef<ChatEntry[]>([])
  const streamingRef = useRef(false)
  const mountedRef = useRef(true)
  const abortRef = useRef<AbortController | null>(null)

  const writeEntries = useCallback((next: ChatEntry[]) => {
    entriesRef.current = next
    setEntries(next)
  }, [])
  const writeStreaming = useCallback((next: boolean) => {
    streamingRef.current = next
    setStreaming(next)
  }, [])

  const stopStream = useCallback(() => {
    abortRef.current?.abort()
    abortRef.current = null
  }, [])

  // Mirrors the page hook's startStream: one reconnect with the latest
  // sinceId so an HMR rebuild or network blip doesn't strand streaming=true.
  const startStream = useCallback(
    (path: string[], streamOpts: { initialSinceId?: string } = {}) => {
      stopStream()
      const controller = new AbortController()
      abortRef.current = controller

      const run = async (sinceId: string | undefined, isRetry: boolean): Promise<void> => {
        try {
          const iter = await client.assistantStream(
            { threadPath: path, ...(sinceId !== undefined && { sinceId }) },
            { signal: controller.signal },
          )
          for await (const ev of iter) {
            if (!mountedRef.current || controller.signal.aborted) return
            if (ev.entries.length > 0) writeEntries(mergeEntries(entriesRef.current, ev.entries))
            writeStreaming(ev.streaming !== undefined)
            if (!ev.streaming) return
          }
        } catch (err) {
          if (controller.signal.aborted || !mountedRef.current) return
          if (!isRetry) {
            logWarning('scribe-assistant', 'stream error, reconnecting', getErrorMessage(err))
            await run(entriesRef.current[entriesRef.current.length - 1]?.id, true)
            return
          }
          logError('scribe-assistant', 'stream failed after retry', err)
          writeStreaming(false)
        }
      }

      void run(streamOpts.initialSinceId, false)
    },
    [client, stopStream, writeEntries, writeStreaming],
  )

  const attach = useCallback(
    async (path: string[]) => {
      try {
        const res = await client.assistantGet({ threadPath: path })
        if (!mountedRef.current) return
        writeEntries(res.conversation.entries)
        const isStreaming = res.conversation.streaming !== undefined
        writeStreaming(isStreaming)
        if (isStreaming) {
          const lastId = res.conversation.entries[res.conversation.entries.length - 1]?.id
          startStream(path, { initialSinceId: lastId })
        }
      } catch (err) {
        if (!mountedRef.current) return
        if (isUnavailableConversationError(err)) {
          // Expired mid-call (Redis TTL) — drop it; the next question starts fresh.
          onThreadPathChange(null)
          return
        }
        logError('scribe-assistant', 'failed to load conversation', err)
        setError('The conversation failed to load.')
      }
    },
    [client, onThreadPathChange, startStream, writeEntries, writeStreaming],
  )

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      stopStream()
    }
  }, [stopStream])

  // Remount with an existing thread (rail↔fullscreen or route change during a
  // call) → reload it. Our own lazy start already seeded entries, so it never
  // re-fetches the thread it just created.
  useEffect(() => {
    if (threadPath && entriesRef.current.length === 0) void attach(threadPath)
  }, [threadPath, attach])

  const send = useCallback(
    async (text: string): Promise<'sent' | 'already_streaming'> => {
      const trimmed = text.trim()
      if (!trimmed) return 'sent'
      if (streamingRef.current) return 'already_streaming'
      setError(null)
      writeStreaming(true)
      try {
        let path = threadPath
        if (!path) {
          const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
          const res = await client.assistantStart({
            context: { patientId },
            ...(timezone ? { timezone } : {}),
          })
          path = [res.conversation.threadId]
          writeEntries(res.conversation.entries)
          onThreadPathChange(path)
        }
        const res = await client.assistantSend({ threadPath: path, text: trimmed })
        if (res.status === 'already_streaming') {
          // Another tab is driving a turn on this conversation — attach to it.
          startStream(path)
          return 'already_streaming'
        }
        writeEntries([
          ...entriesRef.current,
          {
            id: res.turnId,
            kind: 'turn',
            user: { text: trimmed },
            createdAt: new Date().toISOString(),
          },
        ])
        startStream(path, { initialSinceId: res.turnId })
        return 'sent'
      } catch (err) {
        writeStreaming(false)
        if (isUnavailableConversationError(err)) {
          onThreadPathChange(null)
          writeEntries([])
          setError('That conversation expired — ask again to start a new one.')
        } else {
          logError('scribe-assistant', 'send failed', err)
          setError(getErrorMessage(err))
        }
        // 'already_streaming' so the input preserves the typed text for retry.
        return 'already_streaming'
      }
    },
    [client, onThreadPathChange, patientId, startStream, threadPath, writeEntries, writeStreaming],
  )

  const cancel = useCallback(async () => {
    if (!threadPath || !streamingRef.current) return
    try {
      await client.assistantCancel({ threadPath })
      // Don't flip `streaming` locally — the SSE terminal frame does.
    } catch (err) {
      logError('scribe-assistant', 'failed to cancel', err)
    }
  }, [client, threadPath])

  return { entries, streaming, error, send, cancel }
}

// Copy of the page hook's module-private mergeEntries (useAssistant.tsx) —
// id-keyed upsert preserving order; in-flight turns re-arrive whole.
function mergeEntries(prev: ChatEntry[], incoming: ChatEntry[]): ChatEntry[] {
  if (incoming.length === 0) return prev
  const byId = new Map<string, number>()
  prev.forEach((entry, idx) => byId.set(entry.id, idx))

  const next = [...prev]
  for (const entry of incoming) {
    const existingIdx = byId.get(entry.id)
    if (existingIdx !== undefined) {
      next[existingIdx] = entry
      continue
    }
    next.push(entry)
    byId.set(entry.id, next.length - 1)
  }
  return next
}
